// Workspace-level artifact library (user request 2026-08-13): a
// Figma-style hierarchy — left rail lists decks (files), main area shows
// the selected deck's current-version pages as a thumbnail grid with
// reference/view badges. Backed by the existing slide-catalog endpoint;
// clicking a page opens the generic viewer, "Open deck" jumps into it.
import { useEffect, useMemo, useState } from 'react';
import { ArrowBendUpLeft, Eye } from '@phosphor-icons/react';
import { targetFromPreviewUrl, type ViewerTarget } from './ArtifactViewer';
import SlideThumb from './SlideThumb';

interface CatalogSlide {
  slide_num: number;
  references: number;
  views: number;
  preview_url: string | null;
}
interface CatalogDeck {
  deck_id: string;
  title: string;
  version: string;
  slides: CatalogSlide[];
}

export default function WorkspaceLibrary({
  workspaceId,
  onOpenDeck,
  onOpenArtifact,
  onClose,
}: {
  workspaceId: string;
  onOpenDeck: (deckId: string) => void;
  onOpenArtifact: (t: ViewerTarget) => void;
  onClose: () => void;
}) {
  const [decks, setDecks] = useState<CatalogDeck[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeDeckId, setActiveDeckId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/workspaces/${workspaceId}/slide-catalog`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((d: { decks: CatalogDeck[] }) => {
        if (cancelled) return;
        setDecks(d.decks);
        setActiveDeckId(d.decks[0]?.deck_id ?? null);
      })
      .catch(e => { if (!cancelled) setError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [workspaceId]);

  const active = useMemo(
    () => decks?.find(d => d.deck_id === activeDeckId) ?? null,
    [decks, activeDeckId],
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'var(--ds-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--bg-primary, #fff)', borderRadius: 14, width: 'min(1180px, 95vw)', height: 'min(760px, 88vh)', display: 'flex', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        {/* Left rail — decks (Figma "files") */}
        <div style={{ width: 250, borderRight: '1px solid var(--ds-surface-subtle)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', fontWeight: 700, fontSize: 14, borderBottom: '1px solid var(--ds-surface-subtle)' }}>Workspace Library</div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {error && <div style={{ color: 'var(--ds-danger)', fontSize: 12, padding: 8 }}>{error}</div>}
            {!decks && !error && <div style={{ opacity: 0.6, fontSize: 12, padding: 8 }}>Loading…</div>}
            {decks && decks.length === 0 && <div style={{ opacity: 0.6, fontSize: 12, padding: 8 }}>No decks with built slides yet.</div>}
            {decks?.map(d => (
              <div
                key={d.deck_id}
                onClick={() => setActiveDeckId(d.deck_id)}
                style={{
                  padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13, marginBottom: 2,
                  background: d.deck_id === activeDeckId ? 'var(--ds-accent-soft)' : 'none',
                }}
              >
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: d.deck_id === activeDeckId ? 600 : 400 }}>{d.title}</div>
                <div style={{ fontSize: 11, opacity: 0.55 }}>{d.version} · {d.slides.length} pages</div>
              </div>
            ))}
          </div>
        </div>
        {/* Main — pages grid (Figma "pages/frames") */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid var(--ds-surface-subtle)' }}>
            <div style={{ fontSize: 13 }}>
              {active ? (<><strong>{active.title}</strong> <span style={{ opacity: 0.55 }}>· {active.version}</span></>) : ''}
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {active && (
                <button onClick={() => onOpenDeck(active.deck_id)} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid var(--ds-overlay)', cursor: 'pointer', background: 'none' }}>
                  Open deck →
                </button>
              )}
              <button onClick={onClose} aria-label="Close library" style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
            {active && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
                {active.slides.map(s => (
                  <div
                    key={s.slide_num}
                    onClick={() => {
                      const t = s.preview_url ? targetFromPreviewUrl(active.deck_id, s.preview_url) : null;
                      if (t) onOpenArtifact(t);
                    }}
                    style={{ border: '1px solid var(--ds-overlay)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', background: 'var(--ds-surface)' }}
                  >
                    {s.preview_url ? (
                      <SlideThumb src={s.preview_url} title={`${active.title} p${s.slide_num}`} />
                    ) : (
                      <div style={{ aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>Page {s.slide_num}</div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', fontSize: 12 }}>
                      <span>Page {s.slide_num}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, opacity: 0.6 }} title="references · views">
                        <ArrowBendUpLeft size={11} weight="light" /> {s.references}
                        <span style={{ opacity: 0.5, margin: '0 2px' }}>·</span>
                        <Eye size={11} weight="light" /> {s.views}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
